import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { researchosService } from '../../services/researchosService';
import CompanySearch from './company/CompanySearch';

// ResearchOS landing — search first, then one LIVE tile per module (GET /api/researchos/home/ — each headline read
// from the module's own store; a module the caller cannot open is never shown). No descriptions: the number is the
// description. Every tile opens its module; a named thing inside a tile (a mover, a call) opens that thing.

const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(String(v).length === 10 ? `${v}T12:00:00Z` : v);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'America/New_York' });   // market events read on the New York clock
};
const fmtInt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(2)}`;
const clr = (n) => (n > 0 ? 'text-green-500 dark:text-green-400' : n < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-400');
const KIND = { earnings_call: 'Earnings', analyst_call: 'Analyst', conference: 'Conference', investor_day: 'Investor day' };
const humanType = (t) => (t || '').replace(/_/g, ' ');

function Spark({ values }) {
  if (!values || values.length < 2) return null;
  const w = 100, h = 28, max = Math.max(...values, 1);
  const pts = values.map((v, i) => [(i / (values.length - 1)) * w, h - 2 - (v / max) * (h - 4)]);
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join('');
  const [lx, ly] = pts[pts.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-7 w-full text-brand-gold" aria-hidden="true">
      <path d={`${line}L${w},${h}L0,${h}Z`} fill="currentColor" opacity="0.12" />
      <path d={line} fill="none" stroke="currentColor" strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
      <circle cx={lx} cy={ly} r="1.8" fill="currentColor" />
    </svg>
  );
}

// One module tile. The whole tile is a button (stretched over the card); a `link` inside sits above it and opens
// its own target — never a button inside a button.
function Tile({ label, value, unit, foot, extra, link, onOpen, loading, alert }) {
  return (
    <div className="group relative flex min-h-[132px] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-3.5 transition-colors hover:border-brand-gold/60 dark:border-gray-800 dark:from-[#15171d] dark:to-[#111318] sm:p-4">
      <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/60 to-transparent opacity-50 transition-opacity group-hover:opacity-100" />
      <button type="button" onClick={onOpen} aria-label={`Open ${label}`}
        className="absolute inset-0 z-0 touch-manipulation rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/60" />
      <div className="pointer-events-none relative flex items-center justify-between">
        <span className="font-serif text-[15px] tracking-wide text-gray-900 dark:text-gray-100">{label}</span>
        <span className="flex items-center gap-1.5">
          {alert && <span className="h-1.5 w-1.5 rounded-full bg-red-500" title={alert} />}
          <span className="text-gray-300 transition-colors group-hover:text-brand-gold dark:text-gray-600">›</span>
        </span>
      </div>
      <div className="pointer-events-none relative mt-2">
        {loading ? <div className="skeleton h-7 w-16 rounded" />
          : <div className="truncate font-mono text-[22px] leading-7 tabular-nums text-gray-900 dark:text-gray-100">{value}</div>}
        {unit && <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-gray-400">{unit}</div>}
      </div>
      {extra && <div className="pointer-events-none relative mt-2">{extra}</div>}
      <div className="relative mt-auto flex min-h-[20px] items-center justify-between gap-2 pt-2 text-[11px] text-gray-500 dark:text-gray-400">
        <span className="pointer-events-none truncate">{foot}</span>
        {link}
      </div>
    </div>
  );
}

function ChipLink({ onClick, children, title }) {
  return (
    <button type="button" title={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="relative z-10 -my-1.5 shrink-0 touch-manipulation rounded-md px-1.5 py-1.5 font-mono text-[11px] tabular-nums hover:bg-brand-gold/10">
      {children}
    </button>
  );
}

export default function ResearchOSHome({ isAdmin }) {
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    researchosService.getHome()
      .then((r) => { if (alive) setT(r.data || {}); })
      .catch(() => { if (alive) { setT({}); setFailed(true); } });
    return () => { alive = false; };
  }, []);

  const loading = t === null;
  const q = t?.quorum, ch = t?.chatter, po = t?.podium, ca = t?.cadence, at = t?.atlas, co = t?.courier, fo = t?.folio;
  const mover = ch?.top_mover;

  const tiles = [
    <Tile key="quorum" label="Quorum" loading={loading} onOpen={() => nav('/researchos/scores')}
      value={fmtInt(q?.covered)} unit="names scored"
      foot={q?.last_scored ? `last ${fmtDate(q.last_scored)}` : (q ? 'none in your scope' : '')} />,
    <Tile key="chatter" label="Chatter" loading={loading}
      onOpen={() => nav(isAdmin ? '/researchos/chatter-new' : '/researchos/chatter')}
      value={ch ? fmtInt(ch.docs_7d) : '—'} unit={ch ? 'posts · 7d' : null}
      extra={ch?.spark ? <Spark values={ch.spark} /> : null}
      foot={mover ? 'mover' : (ch ? '30-day volume' : '')}
      link={mover && (
        <ChipLink onClick={() => nav(`/researchos/company/${encodeURIComponent(mover.ticker)}`)}
          title={`Chatter rail ${mover.level} · ${ch.mover_lookback_days}-day change`}>
          <span className="text-gray-900 dark:text-gray-100">{mover.ticker}</span>{' '}
          <span className={clr(mover.change)}>{signed(mover.change)}</span>
        </ChipLink>
      )} />,
  ];
  if (isAdmin) {
    const call = po?.latest;
    const brief = co?.latest;
    tiles.push(
      <Tile key="podium" label="Podium" loading={loading} onOpen={() => nav('/researchos/podium')}
        value={fmtInt(po?.calls)} unit="calls"
        foot={call ? fmtDate(call.held_at) || '' : ''}
        link={call && (
          <ChipLink onClick={() => nav(`/researchos/podium/calls/${encodeURIComponent(call.slug)}`)}
            title={`${KIND[call.kind] || 'Call'} · ${call.ticker} ${call.fiscal_label}`}>
            {call.ticker} {call.fiscal_label}
          </ChipLink>
        )} />,
      <Tile key="cadence" label="Cadence" loading={loading} onOpen={() => nav('/researchos/cadence')}
        value={fmtInt(ca?.reports_7d)} unit="reports · 7d"
        foot={ca?.last_report ? `latest ${fmtDate(ca.last_report)}` : ''} />,
      <Tile key="atlas" label="Atlas" loading={loading} onOpen={() => nav('/researchos/atlas')}
        value={at?.quadrant || '—'} unit="macro regime"
        foot={at?.as_of ? `as of ${fmtDate(at.as_of)}` : ''} />,
      <Tile key="courier" label="Courier" loading={loading} onOpen={() => nav('/researchos/briefs')}
        value={fmtInt(co?.briefs_7d)} unit="briefs · 7d"
        alert={brief?.send_error ? `Last send failed: ${brief.send_error}` : null}
        foot={brief ? `${humanType(brief.type)} · ${fmtDate(brief.brief_date)}` : ''} />,
      <Tile key="folio" label="Folio" loading={loading} onOpen={() => nav('/researchos/folio?tab=review&scope=all')}
        value={fmtInt(fo?.needs_you)} unit="questions need you"
        alert={fo?.needs_you ? `${fo.needs_you} fundamentals question(s) need you` : null}
        foot={fo ? `${fmtInt(fo.companies)} ${fo.companies === 1 ? 'company' : 'companies'} covered` : ''} />,
    );
  }

  return (
    <div className="space-y-4">
      <div className="max-w-xl"><CompanySearch /></div>
      <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-3">{tiles}</div>
      {failed && <div className="text-xs text-red-500">Live numbers did not load — the modules still open.</div>}
    </div>
  );
}
